import { Score } from 'modules/core/supabase/tables/match-chronology';
import { Fouls } from 'modules/overlay-v2/domain/interfaces/game-info/state';
import { parseEvent, setFoulsOnPeriod, setGoals } from 'modules/overlay-v2/store/game-info/game-info';
import { setActiveBoard } from 'modules/template-editor/store/templates/templates';
import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import supabase from 'modules/core/supabase/supabase';
import { MatchEvent } from 'shared/types/match-event';
import { getFoulsByPeriod } from 'modules/overlay-v2/domain/utils/get-fouls';
import {
  addEvents,
  setActiveEventId,
  setEvents,
  setIsMatchAvailable,
  setPassedEventsId,
} from 'modules/overlay-v2/store/overlay/overlay';
import {
  selectBroadCastState,
  selectIsBoardLocked,
  selectIsBroadcastInitialized,
  selectIsMatchDataReady,
  selectPassedEventsId,
} from 'modules/overlay-v2/store/overlay/selectors';
import { selectHasGoneLive } from 'modules/overlay-v2/store/readiness/selectors';
import { selectPeriod } from 'modules/overlay-v2/store/time/selectors';
import { setPeriod, setTimer } from 'modules/overlay-v2/store/time/time';
import useChronologyFallbackPoll from 'modules/overlay-v2/ui/hooks/use-chronology-fallback-poll';

function useInitSubscribeOverlayV2() {
  const dispatch = useAppDispatch();

  const { matchId } = useAppSelector(selectBroadCastState);
  const isBoardLocked = useAppSelector(selectIsBoardLocked);
  const statePeriod = useAppSelector(selectPeriod);
  const isBroadcastInitialized = useAppSelector(selectIsBroadcastInitialized);
  const hasGoneLive = useAppSelector(selectHasGoneLive);
  const isMatchDataReady = useAppSelector(selectIsMatchDataReady);
  const passedEventsId = useAppSelector(selectPassedEventsId);
  const passedEventsIdRef = useRef(passedEventsId);
  useEffect(() => {
    passedEventsIdRef.current = passedEventsId;
  }, [passedEventsId]);

  // Пока канал журнала лежит, состояние догоняется запросами: сам он не восстановится.
  useChronologyFallbackPoll();

  useEffect(() => {
    if (!isBroadcastInitialized) return;
    dispatch(setIsMatchAvailable(matchId !== undefined));
    if (matchId !== undefined) return;
    // В эфире пропажа матча табло не гасит и время не обнуляет: перезапуск трансляции в
    // приложении — это `DELETE` + `INSERT` одной и той же строки, и промежуток между ними
    // не повод стирать эфир (решение Р3 плана IMGM-1435). Основную защиту ставит
    // `use-init-broadcast`, эта — на случай других путей пропажи `matchId`.
    if (hasGoneLive) return;
    dispatch(setActiveBoard({ id: undefined }));
    dispatch(setEvents([]));
    dispatch(setPassedEventsId([]));
    dispatch(setActiveEventId(null));
    dispatch(setTimer({ milliseconds: 0 }));
    dispatch(setPeriod(1));
  }, [dispatch, matchId, isBroadcastInitialized, hasGoneLive]);

  useEffect(() => {
    if (matchId === undefined) return;

    supabase.matchChronology.observeChanges(matchId);

    return () => {
      supabase.matchChronology.stopObserveChanges();
    };
  }, [matchId]);

  useEffect(() => {
    if (matchId === undefined) return;

    const scoreCallback = (score: Score | null) => {
      if (!score) {
        supabase.overlayLogs.createWarning({
          message: 'No score received',
        });
        return;
      }
      dispatch(setGoals({ first: score[0].score, second: score[1].score }));
    };

    supabase.matchChronology.score.subscribe(scoreCallback);

    return () => {
      supabase.matchChronology.score.unsubscribe(scoreCallback);
    };
  }, [dispatch, matchId]);

  useEffect(() => {
    if (matchId === undefined) return;
    if (!isMatchDataReady) return;

    const activeBoardCallback = (id: number | null) => {
      if (isBoardLocked) return;
      dispatch(setActiveBoard({ id: id ?? undefined }));
    };

    const eventsCallback = (newEvents: MatchEvent[]) => {
      dispatch(parseEvent({ events: newEvents }));
      if (isBoardLocked) return;
      const knownCount = passedEventsIdRef.current.length;
      const trulyNewEvents = newEvents.slice(knownCount);
      if (trulyNewEvents.length > 0) {
        dispatch(addEvents(trulyNewEvents));
      }
    };

    supabase.matchChronology.activeBoardId.subscribe(activeBoardCallback);
    supabase.matchChronology.events.subscribe(eventsCallback);

    return () => {
      supabase.matchChronology.activeBoardId.unsubscribe(activeBoardCallback);
      supabase.matchChronology.events.unsubscribe(eventsCallback);
    };
  }, [dispatch, isBoardLocked, isMatchDataReady, matchId]);

  useEffect(() => {
    if (matchId === undefined) return;

    const timeCallback = ({ seconds, period }: { seconds: number; period: number }) => {
      dispatch(setTimer({ milliseconds: seconds * 1000 }));
      dispatch(setPeriod(period));
      if (statePeriod !== period) {
        getFoulsByPeriod(matchId, period).then((fouls: Fouls) => {
          dispatch(setFoulsOnPeriod({ ...fouls, period }));
        });
      }
    };

    supabase.matchChronology.currentTime.subscribe(timeCallback);

    return () => {
      supabase.matchChronology.currentTime.unsubscribe(timeCallback);
    };
  }, [dispatch, matchId, statePeriod]);
}

export default useInitSubscribeOverlayV2;
